import React, { useEffect, useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";

import ResizeChild from "./ResizeChild";
import {
  SharedResizingProvider,
  useSharedResizing,
} from "./SharedResizingContext";

/**
 * This is a container that manages smooth swiping animation between child components for a wizard-like stepped interface. You can set a defaultWidth for the container OR you can manually set the width of the children like we are doing in RemoveWithRefund.js. When manually setting the width, you need to make it conditional based on screen size in order to handle smaller screens. This does not work well if you are setting the width of the children to 100%, so you need to set to hard values, like "500px." Additionally, it helps if you make sure to set the parent container to box-sizing: border-box to prevent the container overflowing the screen on smaller devices.
 * @summary A component to manage smooth swiping animation between child components for a wizard-like stepped interface.
 * @component
 * @param {string} defaultWidth
 */

const SharedResizing = ({ children, defaultWidth = "300px" }) => {
  return (
    <SharedResizingProvider>
      <SharedResizingContainer defaultWidth={defaultWidth}>
        {children}
      </SharedResizingContainer>
    </SharedResizingProvider>
  );
};

function SharedResizingContainer({ children, defaultWidth = "300px" }) {
  const { currentKey, direction } = useSharedResizing();

  const childRef = useRef();
  const [dimensions, setDimensions] = useState();

  // KNOWN ISSUE: if the children prop is a fragment, the next couple of lines don't really work.
  // There is probably a solution that involves using React.Children.toArray(children), but we have been able to avoid that so far.
  // Additionally, I would like it if there was a 'loading' prop that passed a loading component instead of the child.

  // if there's only 1 child then it's not an array
  const child = Array.isArray(children)
    ? children.find((child) => parseInt(child.key, 10) === currentKey)
    : children;

  useEffect(
    function updateDimensionsOnNewCurrentKey() {
      /**
       * The contents of the interval were running so fast
       * that the dimensions were updating to the same values
       * and stopping the animation before it reached the full height and width.
       * Removing the if-statement causes race conditions with piles of renders.
       * So I put it in an interval to slow down the updating of the values and the race conditions.
       * */
      const interval = setInterval(() => {
        const { width = 0, height = 0 } =
          childRef.current?.getBoundingClientRect() || {};

        /** width/height have like 10 decimals.
         * Rounding to whole numbers prevents errors because of a difference of .00000001,
         * which is an irrelevant difference here.
         */
        const roundWidth = Math.round(width);
        const roundHeight = Math.round(height);

        if (width && height) {
          if (
            dimensions &&
            roundWidth === dimensions.width &&
            roundHeight === dimensions.height
          ) {
            clearInterval(interval);
            return;
          }

          setDimensions({ width: roundWidth, height: roundHeight });
        }
      }, 100);

      return () => clearInterval(interval);
    },
    [currentKey, dimensions]
  );

  return (
    <motion.div
      style={{
        position: "relative",
        overflow: "hidden",
      }}
      animate={{
        height: dimensions?.height ? dimensions.height : "auto",
        width: dimensions?.width ? dimensions.width : defaultWidth,
      }}
    >
      <AnimatePresence custom={direction}>
        <ResizeChild
          key={child.key}
          childRef={childRef}
          direction={direction}
          variants={variants}
        >
          {child}
        </ResizeChild>
        );
      </AnimatePresence>
    </motion.div>
  );
}

export const variants = {
  enter: (direction) => {
    return {
      x: direction === "left" ? 1000 : -1000,
    };
  },
  animate: {
    zIndex: 1,
    x: 0,
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction === "left" ? -1000 : 1000,
    };
  },
};

export default SharedResizing;
